import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';

import { TableHeaderWithTooltipIcon, TextButtonCell, TooltipTextCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import { COLLECTING_REQUEST_STATUS_BADGE_COLOR, DATE_FORMAT } from '~/constants';
import { COLLECTING_SHIPPING_LABEL_NUMBER_TOOLTIP } from '~/constants/delivery-agency';

import type { CollectingRequestListItem } from '~/types/delivery-agency.types';

const getDeliveryAgencyCollectingRequestsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (collectingRequest: CollectingRequestListItem) => void;
}) => {
  const columnDefs: ColDef<CollectingRequestListItem>[] = [
    {
      colId: 'rowNumber',
      headerName: 'No',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      valueGetter: ({ node }) => (node?.rowIndex ?? 0) + 1 + currentPage * pageSize,
      width: 88,
    },
    {
      field: 'collectingRequestedDate',
      headerName: '반품접수일',
      width: 110,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingRequestListItem, CollectingRequestListItem['collectingRequestedDate']>) =>
        formatDate(value, DATE_FORMAT.DATE) ?? '-',
    },
    {
      field: 'collectingRequestStatus',
      headerName: '회수 상태',
      cellDataType: false,
      width: 120,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<CollectingRequestListItem, CollectingRequestListItem['collectingRequestStatus']>) => ({
        variant: 'light',
        status: true,
        color: value ? COLLECTING_REQUEST_STATUS_BADGE_COLOR[value.code] : undefined,
        children: value?.description || '-',
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'statusUpdatedAt',
      headerName: '상태 마지막 업데이트 일시',
      width: 190,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingRequestListItem, CollectingRequestListItem['statusUpdatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'collectingRequestCode',
      headerName: '반품요청번호',
      width: 160,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<CollectingRequestListItem, CollectingRequestListItem['collectingRequestCode']>) => ({
        children: data?.collectingRequestCode ?? '-',
        onClick: ({ data }: IRowNode<CollectingRequestListItem>) => {
          if (!data) return;
          onClickViewDetail(data);
        },
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'collectingShippingLabelNumbers',
      headerName: '회수 운송장번호',
      width: 180,
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: { tooltipDescription: COLLECTING_SHIPPING_LABEL_NUMBER_TOOLTIP },
      valueFormatter: ({
        value,
      }: ValueFormatterParams<
        CollectingRequestListItem,
        CollectingRequestListItem['collectingShippingLabelNumbers']
      >) => {
        // 채번 전에는 빈 값 원소가 내려와 그대로 이으면 구분자만 남는다
        const numbers = value?.filter(Boolean) ?? [];
        return numbers.length ? numbers.join(', ') : '-';
      },
      cellRenderer: TooltipTextCell,
    },
    {
      field: 'productName',
      headerName: '회수할 상품명',
      width: 180,
      cellRenderer: TooltipTextCell,
    },
    {
      field: 'collectedProductHandling',
      headerName: '회수한 상품 처리 방식',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingRequestListItem, CollectingRequestListItem['collectedProductHandling']>) =>
        value?.description || '-',
    },
    {
      field: 'contactName',
      headerName: '원주문 수령자명',
      width: 120,
    },
    {
      field: 'originalOutboundCode',
      headerName: '출고번호',
      width: 160,
    },
    {
      field: 'clientOrderCodes',
      headerName: '판매처 주문번호(원주문)',
      flex: 1,
      minWidth: 190,
      width: 190,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingRequestListItem, CollectingRequestListItem['clientOrderCodes']>) =>
        value?.length ? value.join(', ') : '-',
      cellRenderer: TooltipTextCell,
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyCollectingRequestsColumnDefs;
